//
// # GenAlpha SPICE-Class Simulation Schema
//
// TypeScript bindings for the spice-class simulation **input + result** schema. Manually
// written to be JSON-schema-equivalent to the Rust source of
// truth in `crates/schema/src/current/sim.rs` — same field names, same discriminated-union tags
// (see `schema/readme.md`: the bindings in each language are hand-tuned and kept in lockstep).
//
// Invariant: a simulation is a **list of analyses → list of
// results**, and `result[i]` is the result of `analyses[i]`. Every backend preserves this
// ordering (`SimResult.analyses` mirrors `Sim.analyses`).
//
// House style mirrors the Rust serde form: discriminated unions carry a `kind` tag
// (`"op"`/`"dc"`/…), camelCase field names so the JSON wire form is identical across TS &
// Rust, and collection/optional fields that have a serde `default`/`skip_serializing_if` are
// optional here (a minimal `{ schemaVersion, tb }` validates, exactly like the Rust side).

// Local Imports
import * as core from "./core";
import { SchemaVersion } from "./version";

// # Sim
// A spice-class simulation: a testbench + an ordered list of analyses + control.
//
// `tb` is just an `Ident`: the top of a sim is **not** required to be a
// module/circuit with ports. The netlister resolves it to one-or-more `Circuit`s.
export interface Sim {
  schemaVersion: SchemaVersion;
  // Testbench top. Resolved to `Circuit`(s) by the netlister; NOT required to have ports.
  tb: core.Ident;
  // Ordered analyses. `SimResult.analyses[i]` corresponds to `analyses[i]`.
  analyses?: Array<Analysis>;
  // Sim-wide control elements (applied to all analyses). Request-safe subset only.
  controls?: Array<Control>;
  // Global options (`.option`), open-ended name/value.
  options?: Array<SimOption>;
  // Top-level `.param` defaults / overrides.
  params?: Array<Param>;
  // PDK this sim targets (e.g. `"gf12"`), when it is a PDK-bound run. The server
  // resolves the model library + device mapping from this; the client names the
  // PDK, never filesystem paths.
  pdk?: string;
  // Process corners to simulate (e.g. `["TT"]`, `["FF","SS"]`). The server's
  // `HspicePdk` impl maps each to a model `.lib` section — the client names
  // corners, never file paths.
  corners?: Array<Corner>;
  name?: string;
}

// # Corner
// A process corner. The common corners are the named values (`TT`/`FF`/`SS`/`SF`/`FS`
// and their `G` "global" variants); anything a specific PDK defines beyond them
// (e.g. GF12's `FFP`/`SSP`, GF22's `FFE`/`SSE`) is any other string. The wire form
// is a plain uppercase string. (Twin of the Rust `Corner` enum.)
export type Corner =
  | "TT"
  | "FF"
  | "SS"
  | "SF"
  | "FS"
  | "TTG"
  | "FFG"
  | "SSG"
  | "SFG"
  | "FSG"
  // Any other PDK-specific corner name, carried verbatim. The `& {}` keeps the
  // named values as autocomplete hints without collapsing the union to `string`.
  | (string & {});

// # Analysis
// An analysis to run. `sweep`
// and `monte` nest an `inner` list of analyses (analysis-sweep / Monte Carlo).
export type Analysis =
  | { kind: "op"; name?: string }
  | ({ kind: "dc" } & DcAnalysis)
  | ({ kind: "ac" } & AcAnalysis)
  | ({ kind: "tran" } & TranAnalysis)
  | ({ kind: "noise" } & NoiseAnalysis)
  // Analysis-sweep: re-run `inner` analyses over `var` (HSPICE/ngspice `.step`).
  | ({ kind: "sweep" } & SweepAnalysis)
  // Monte Carlo: re-run `inner` analyses `npts` times.
  | ({ kind: "monte" } & MonteAnalysis)
  // Raw passthrough analysis command.
  | { kind: "custom"; cmd: string; name?: string };

export type AnalysisKind = Analysis["kind"];

// # DC sweep analysis (`.dc <var> …`).
export interface DcAnalysis {
  var: string; // Swept variable (source or `.param` name).
  sweep: Sweep;
  name?: string;
}

// # AC small-signal analysis (`.ac …`). Always a frequency `LogSweep`.
export interface AcAnalysis {
  sweep: LogSweep;
  name?: string;
}

// # Transient analysis (`.tran <tstep> <tstop>`).
export interface TranAnalysis {
  tstop: number;
  tstep?: number; // Optional max time step; backends pick a default when absent.
  ic?: Record<string, number>; // Initial conditions: node name -> volts (`.ic v(node)=…`).
  name?: string;
}

// # Noise analysis (`.noise …`). Output is a node pair; input is a source name.
export interface NoiseAnalysis {
  outputP: string; // Positive output node.
  outputN?: string; // Negative output node (defaults to ground `0` when absent).
  inputSource: string; // Input source name (the noise reference).
  sweep: LogSweep;
  name?: string;
}

// # Analysis-sweep: re-run `inner` analyses while sweeping `var` (HSPICE/ngspice `.step`).
export interface SweepAnalysis {
  var: string; // Swept variable (`.param` or device/source name).
  sweep: Sweep;
  inner?: Array<Analysis>; // Analyses re-run at every sweep point.
  name?: string;
}

// # Monte Carlo: re-run `inner` analyses `npts` times with statistical variation.
export interface MonteAnalysis {
  npts: number;
  seed?: number; // Optional RNG seed for reproducibility.
  inner?: Array<Analysis>; // Analyses re-run on each Monte point.
  name?: string;
}

// # Sweep
// A value sweep: `linear`/`log`/`points`. Used by DC and analysis-sweep.
export type Sweep =
  | { kind: "linear"; start: number; stop: number; step: number }
  | { kind: "log"; start: number; stop: number; npts: number }
  | { kind: "points"; points: Array<number> };

// # LogSweep
// A logarithmic frequency sweep `(fstart, fstop, npts)` — the AC/Noise sweep shape.
export interface LogSweep {
  fstart: number;
  fstop: number;
  npts: number; // Points per decade.
}

// # Control
// Sim-wide control element — the **request-safe** subset only.
//
// Deliberately **no** `include`/`lib` filesystem cards: a web client must not be able to
// point the simulator at arbitrary server paths. The PDK model/corner preamble is
// synthesized server-side by the `HspicePdk` impl from `(pdk, corners)`.
// `literal` is the one escape hatch and is monitored (logged + counted) where it is emitted
// into a deck.
export type Control =
  | ({ kind: "save" } & Save)
  // Analysis this measurement applies to (e.g. "tran", or an analysis `name`).
  | { kind: "meas"; analysis: string; name: string; expr: string }
  | ({ kind: "param" } & Param)
  // Raw passthrough "out-card" emitted verbatim into the deck. MONITORED.
  | { kind: "literal"; text: string };

export type ControlKind = Control["kind"];

// # Save
// What to save/probe: either a `SaveMode` or an explicit signal list.
export type Save =
  | { save: "mode"; mode: SaveMode } // Save everything / nothing per `SaveMode`.
  | { save: "signals"; signals: Array<string> }; // Save only the named signals.

// # SaveMode
// Coarse save selection: save nothing / save all signals.
export type SaveMode = "none" | "all";

// # Param
// A `.param` entry — string-valued, like `CircuitInstanceParam`.
export interface Param {
  name: string;
  value: string;
}

// # SimOption
// A `.option` entry — open-ended name/value. Well-known names
// (`temp`, `tnom`, `gmin`, `reltol`, `abstol`, `vntol`, `method`, `seed`) are documented,
// not enum-enforced; backends translate what they support and pass/drop the rest.
export interface SimOption {
  name: string;
  value: string;
}

// ===========================================================================
// Result schema.
//
// Per-analysis result messages, ordered: `SimResult.analyses[i]` is the
// result of `Sim.analyses[i]`. Waveform data is nested one inner array per signal
// (`data[s][p]`), so `data.length === signals.length` and each `data[s]` matches
// the axis length. Each result carries an optional `name`, so a caller can
// cross-check it against the input analysis `name`.
// ===========================================================================

// # SimResult
// Ordered results of a `Sim` — `analyses[i]` is the result of `Sim.analyses[i]`.
// The length and order MUST match the input.
export interface SimResult {
  analyses?: Array<AnalysisResult>; // One result per input analysis, in input order.
}

// # AnalysisResult
// The result of one `Analysis`. The discriminated `kind` mirrors `Analysis` one-for-one
// `sweep`/`monte` nest an ordered `inner` result list, and `custom` is an
// empty slot-filler that keeps the result list aligned with the input.
export type AnalysisResult =
  | ({ kind: "op" } & OpResult)
  | ({ kind: "dc" } & DcResult)
  | ({ kind: "ac" } & AcResult)
  | ({ kind: "tran" } & TranResult)
  | ({ kind: "noise" } & NoiseResult)
  | ({ kind: "sweep" } & SweepResult)
  | ({ kind: "monte" } & MonteResult)
  // Empty slot-filler for a `custom` analysis — keeps `analyses[i]` aligned.
  | { kind: "custom" };

// # OpResult
// Operating-point result: a row of scalar signal values.
export interface OpResult {
  name?: string;
  signals?: Array<string>; // Ordered signal names; `data[i]` is the value of `signals[i]`.
  data?: Array<number>; // Scalar value per signal, in `signals` order.
}

// # DcResult
// DC-sweep result. `data` is nested one inner array per signal: `data[s][p]` is
// signal `s` at sweep point `p`, so `data.length === signals.length`.
export interface DcResult {
  name?: string;
  indepName: string; // Independent (swept) variable name.
  signals?: Array<string>; // Ordered signal names.
  data?: Array<Array<number>>; // One inner array per signal: `data[s][p]`.
  measurements?: Record<string, number>; // Scalar `.meas` results, by measurement name.
}

// # AcResult
// AC small-signal result. `data` is nested one inner array per signal: `data[s][f]`
// is signal `s` at `freq[f]` (a `ComplexNum`).
export interface AcResult {
  name?: string;
  freq?: Array<number>; // Frequency axis (Hz).
  signals?: Array<string>; // Ordered signal names.
  data?: Array<Array<ComplexNum>>; // One inner array per signal: `data[s][f]`.
  measurements?: Record<string, number>; // Scalar `.meas` results, by measurement name.
}

// # TranResult
// Transient result. `data` is nested one inner array per signal: `data[s][t]` is
// signal `s` at `time[t]`, so `data.length === signals.length` and each
// `data[s].length === time.length`.
export interface TranResult {
  name?: string;
  time?: Array<number>; // Time axis (s).
  signals?: Array<string>; // Ordered signal names.
  data?: Array<Array<number>>; // One inner array per signal: `data[s][t]`.
  measurements?: Record<string, number>; // Scalar `.meas` results, by measurement name.
}

// # NoiseResult
// Noise result. `data` is nested one inner array per signal: `data[s][f]` is signal
// `s` at `freq[f]`; `integratedNoise` holds the band-integrated totals per signal.
export interface NoiseResult {
  name?: string;
  freq?: Array<number>; // Frequency axis (Hz).
  signals?: Array<string>; // Ordered signal names (noise spectral densities).
  data?: Array<Array<number>>; // One inner array per signal: `data[s][f]`.
  integratedNoise?: Record<string, number>; // Band-integrated noise per signal name.
  measurements?: Record<string, number>; // Scalar `.meas` results, by measurement name.
}

// # SweepResult
// Analysis-sweep result: the `inner` analyses re-run at each sweep point, ordered like
// `SweepAnalysis.inner`.
export interface SweepResult {
  name?: string;
  inner?: Array<AnalysisResult>; // Child results, in `SweepAnalysis.inner` order.
}

// # MonteResult
// Monte-Carlo result: the `inner` analyses re-run on each point, ordered like
// `MonteAnalysis.inner`.
export interface MonteResult {
  name?: string;
  inner?: Array<AnalysisResult>; // Child results, in `MonteAnalysis.inner` order.
}

// # ComplexNum
// A complex value (`re + j·im`) — the AC data element.
export interface ComplexNum {
  re: number;
  im: number;
}
